import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";
import { PORTFOLIO_COMPANIES } from "@/data/content";

export default function Platforms() {
  useReveal();

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />

      <PageHero
        eyebrow="Investment / Platforms"
        title="Disciplined platforms."
        accent={<><br/>Enduring value.</>}
        sub="Four operating platforms across real estate, healthcare, and wellness — the institutional structures through which we deploy capital, steward strategic assets, and scale long-term value creation."
        bgImage="/assets/1thymdqv_Than-Long.jpg"
      />

      {/* PLATFORMS LIST — clean light-gray bg, image on top, content below */}
      <section className="relative bg-[#f4f5f7] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {PORTFOLIO_COMPANIES.map((p, i) => (
              <Link
                key={p.slug}
                to={`/portfolio/${p.slug}`}
                data-reveal
                style={{ "--reveal-delay": `${i * 130}ms` }}
                className="group block bg-white border border-[#14355a]/10 hover:border-[#14355a]/30 transition-all duration-500 hover:-translate-y-1"
                data-testid={`platform-card-${p.slug}`}
              >
                {/* Image on TOP */}
                <div className="relative aspect-[16/9] overflow-hidden bg-[#14355a]/5">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute top-6 left-6">
                    <span className="inline-block px-3.5 py-1.5 bg-white/95 backdrop-blur-sm font-display text-[10.5px] uppercase tracking-[0.22em] text-[#14355a]" style={{ fontWeight: 500 }}>
                      {p.sector}
                    </span>
                  </div>
                  <ArrowUpRight
                    size={22}
                    className="absolute top-6 right-6 text-white drop-shadow-md group-hover:rotate-12 transition-transform duration-500"
                  />
                </div>

                {/* Content below */}
                <div className="p-8 lg:p-10">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-unik-gold tracking-[0.2em]" style={{ fontSize: "20px", fontWeight: 500 }}>
                      0{i + 1}
                    </span>
                    <span className="w-10 h-px bg-[#14355a]/20" />
                  </div>
                  <h3
                    style={{ fontSize: "clamp(1.75rem, 2.4vw, 2.25rem)", fontWeight: 500 }}
                    className="mt-5 font-display text-[#14355a] tracking-tight leading-tight"
                  >
                    {p.name}
                  </h3>
                  <p className="mt-3 text-[14px] uppercase tracking-[0.14em] text-[#14355a]/55 font-medium">
                    {p.focus}
                  </p>
                  <p className="mt-6 text-[15px] lg:text-[16px] leading-[1.95] text-unik-ink/75">
                    {p.body[0]}
                  </p>
                  <span className="link-rule mt-8 text-[#14355a] text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 group-hover:text-unik-gold">
                    View Platform <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow="Investor Relations"
        title="Partner with"
        accent="Unik."
        body="Explore platform-level partnership and co-investment opportunities with our capital partners team."
        primary={{ label: "Contact Us", to: "/contact" }}
        secondary={{ label: "Investment Overview", to: "/investments" }}
      />

      <SiteFooter />
    </div>
  );
}
